import type {
  BoardActivity,
  BoardActivityRow,
  BoardCell,
  BoardRow,
  BoardService,
  BoardTeam,
  WeeklyBoard,
} from '@obraplan/core/domain/planning/weekly-board';

/**
 * A semana agrupada por equipe (Fase 30, D10).
 *
 * ```text
 * ▾ Alvenaria 01 · 3 pessoas                          seg     ter    semana
 *     ALVENARIA — 3º Pavimento
 *       ASSENTAMENTO                                   24h     24h      48h
 *         Meta da equipe (m²)                          30      30       60
 *           João da Silva                              8h      8h       16h
 *           Maria Souza                                8h      8h       16h
 *           Pedro Lima                                 8h      8h       16h
 * Carlos Souza — Pedreiro                              (a linha de sempre)
 * ```
 *
 * Quem junta é a **marca** da alocação (`plannedTeamId`, RN-422), e não a equipe de hoje da
 * pessoa: quem trocou de equipe na quarta continua na linha da antiga na segunda. A alocação sem
 * marca fica onde sempre esteve, no bloco da pessoa — e a pessoa que só tem alocação da equipe
 * não ganha bloco próprio: ela está na linha da equipe ("quem não tem equipe vem sozinho").
 *
 * Função pura: recebe o quadro do domínio e devolve as linhas. A conta da jornada continua sendo
 * a de cada pessoa (`BoardRow.dayTotals`), e é dela que sai o aviso de quem passa no dia.
 */

/** Um dia da linha da equipe numa frente × serviço: as horas e a meta somadas das pessoas. */
export interface TeamDaySum {
  readonly date: string;
  readonly quarterHours: number;
  /** A meta da equipe no dia, somadas as partes. Ausente quando ninguém tem meta no dia. */
  readonly plannedQuantity?: number;
  /** Quantas pessoas a equipe tem nesta frente × serviço no dia. */
  readonly people: number;
  /** Alguém da equipe passa da jornada neste dia — pelo total do dia dele (RN-044). */
  readonly exceeded: boolean;
}

/** Uma pessoa dentro da linha da equipe: só as células desta equipe; as outras, vazias. */
export interface TeamMemberLine {
  readonly row: BoardRow;
  readonly activityRow: BoardActivityRow;
}

/** Uma frente × serviço da equipe na semana. */
export interface TeamFront {
  readonly key: string;
  readonly activity: BoardActivity;
  readonly service?: BoardService;
  readonly days: readonly TeamDaySum[];
  readonly plannedQuarterHours: number;
  readonly members: readonly TeamMemberLine[];
}

export interface TeamGroup {
  readonly team: BoardTeam;
  /** Quantas pessoas diferentes a equipe teve na semana. */
  readonly people: number;
  readonly plannedQuarterHours: number;
  readonly fronts: readonly TeamFront[];
}

export interface BoardByTeam {
  /** Pelo nome da equipe. Só as equipes com alguma alocação marcada na semana. */
  readonly teams: readonly TeamGroup[];
  /**
   * As linhas de sempre, sem as alocações da equipe.
   *
   * A frente × serviço que mistura dias da equipe e dias da pessoa continua aqui com os dias da
   * pessoa; a célula da equipe fica com a alocação (para a grade dizer que o dia é da equipe, e
   * não oferecer um campo vazio que somaria nela, RN-231). Quem não tem alocação nenhuma
   * continua aqui, como hoje.
   */
  readonly rows: readonly BoardRow[];
}

const chaveDaLinha = (activityRow: BoardActivityRow): string =>
  `${activityRow.activity.activityId}|${activityRow.service?.serviceId ?? 'sem'}`;

const porNome = (a: string, b: string): number => a.localeCompare(b, 'pt-BR');

interface FrenteEmMontagem {
  readonly activity: BoardActivity;
  readonly service?: BoardService;
  readonly pessoas: Map<string, { readonly row: BoardRow; readonly cells: BoardCell[] }>;
}

/** Abre a equipe fechada, fecha a aberta — o estado é da tela, por equipe. */
export function alternar(abertas: ReadonlySet<string>, teamId: string): ReadonlySet<string> {
  const proximas = new Set(abertas);
  if (proximas.has(teamId)) proximas.delete(teamId);
  else proximas.add(teamId);
  return proximas;
}

/** A equipe de cuja alocação a célula é, ou nenhuma (RN-422). */
export function teamOfCell(cell: BoardCell): string | undefined {
  return cell.allocation?.plannedTeamId;
}

export function groupBoardByTeam(board: WeeklyBoard): BoardByTeam {
  const equipes = new Map<string, Map<string, FrenteEmMontagem>>();

  for (const row of board.rows) {
    for (const activityRow of row.activities) {
      const chave = chaveDaLinha(activityRow);
      activityRow.cells.forEach((cell, indice) => {
        const teamId = teamOfCell(cell);
        if (teamId === undefined) return;

        let frentes = equipes.get(teamId);
        if (frentes === undefined) {
          frentes = new Map();
          equipes.set(teamId, frentes);
        }
        let frente = frentes.get(chave);
        if (frente === undefined) {
          frente = {
            activity: activityRow.activity,
            ...(activityRow.service === undefined ? {} : { service: activityRow.service }),
            pessoas: new Map(),
          };
          frentes.set(chave, frente);
        }
        let pessoa = frente.pessoas.get(row.resource.resourceId);
        if (pessoa === undefined) {
          pessoa = { row, cells: activityRow.cells.map((vazia) => ({ date: vazia.date })) };
          frente.pessoas.set(row.resource.resourceId, pessoa);
        }
        pessoa.cells[indice] = cell;
      });
    }
  }

  const nomeDa = new Map(board.teams.map((team) => [team.teamId as string, team]));

  const teams = [...equipes.entries()]
    .map(([teamId, frentes]): TeamGroup => {
      const fronts = [...frentes.entries()]
        .map(([key, frente]): TeamFront => {
          const members = [...frente.pessoas.values()]
            .sort((a, b) => porNome(a.row.resource.name, b.row.resource.name))
            .map((pessoa): TeamMemberLine => ({
              row: pessoa.row,
              activityRow: {
                activity: frente.activity,
                ...(frente.service === undefined ? {} : { service: frente.service }),
                cells: pessoa.cells,
                plannedQuarterHours: pessoa.cells.reduce(
                  (soma, cell) => soma + (cell.allocation?.quarterHours ?? 0),
                  0,
                ),
              },
            }));

          const days = board.days.map((day, indice): TeamDaySum => {
            let quarterHours = 0;
            let plannedQuantity: number | undefined;
            let people = 0;
            let exceeded = false;
            for (const membro of members) {
              const allocation = membro.activityRow.cells[indice]?.allocation;
              if (allocation === undefined) continue;
              people += 1;
              quarterHours += allocation.quarterHours;
              if (allocation.plannedQuantity !== undefined) {
                plannedQuantity = (plannedQuantity ?? 0) + allocation.plannedQuantity;
              }
              if (membro.row.dayTotals[indice]?.status === 'EXCEEDED') exceeded = true;
            }
            return {
              date: day.date,
              quarterHours,
              ...(plannedQuantity === undefined ? {} : { plannedQuantity }),
              people,
              exceeded,
            };
          });

          return {
            key,
            activity: frente.activity,
            ...(frente.service === undefined ? {} : { service: frente.service }),
            days,
            plannedQuarterHours: days.reduce((soma, day) => soma + day.quarterHours, 0),
            members,
          };
        })
        .sort(
          (a, b) =>
            porNome(a.activity.label, b.activity.label) ||
            porNome(a.service?.name ?? '', b.service?.name ?? ''),
        );

      const people = new Set(
        fronts.flatMap((front) => front.members.map((membro) => membro.row.resource.resourceId)),
      ).size;

      return {
        // A lista do quadro nomeia; sem ela (a equipe sumiu do cadastro), a linha ainda aparece.
        team: nomeDa.get(teamId) ?? {
          teamId: teamId as BoardTeam['teamId'],
          name: 'Equipe',
          isActive: false,
        },
        people,
        plannedQuarterHours: fronts.reduce((soma, front) => soma + front.plannedQuarterHours, 0),
        fronts,
      };
    })
    .sort((a, b) => porNome(a.team.name, b.team.name));

  const rows = board.rows.flatMap((row): BoardRow[] => {
    // Sem alocação nenhuma, a pessoa vem como hoje: é quem tem saldo para receber trabalho.
    if (row.activities.length === 0) return [row];

    let mudou = false;
    const activities = row.activities.flatMap((activityRow): BoardActivityRow[] => {
      const daPessoa = activityRow.cells.filter(
        (cell) => cell.allocation !== undefined && teamOfCell(cell) === undefined,
      );
      const daEquipe = activityRow.cells.some((cell) => teamOfCell(cell) !== undefined);
      if (!daEquipe) return [activityRow];
      mudou = true;
      if (daPessoa.length === 0) return [];
      return [
        {
          ...activityRow,
          plannedQuarterHours: daPessoa.reduce(
            (soma, cell) => soma + (cell.allocation?.quarterHours ?? 0),
            0,
          ),
        },
      ];
    });

    if (!mudou) return [row];
    // A semana inteira dela é da equipe: ela está na linha da equipe, e não sozinha.
    if (activities.length === 0) return [];
    return [{ ...row, activities }];
  });

  return { teams, rows };
}
